import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { History as HistoryIcon, Trash2, X, RotateCcw } from "lucide-react";
import { useI18n, RH_LABELS } from "../i18n";

const KEY = "refundaja-riwayat";
const load = () => {
    try {
        return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
        return [];
    }
};
const save = (list) => localStorage.setItem(KEY, JSON.stringify(list));

export function pushHistory(entry) {
    const list = load();
    const l = list[0];
    if (
        l &&
        l.nama === entry.nama &&
        l.harga === entry.harga &&
        l.total === entry.total &&
        l.sisa === entry.sisa &&
        l.rate === entry.rate &&
        l.hasil === entry.hasil
    )
        return;
    save([{ ...entry, id: Date.now(), at: Date.now() }, ...list].slice(0, 20));
    window.dispatchEvent(new Event("refundaja-riwayat"));
}

const fmt = (n) => new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(n);
const ago = (locale, ts) => {
    const s = Math.floor((Date.now() - ts) / 1000);
    try {
        const r = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
        if (s < 60) return r.format(-Math.max(1, s), "second");
        if (s < 3600) return r.format(-Math.floor(s / 60), "minute");
        if (s < 86400) return r.format(-Math.floor(s / 3600), "hour");
        return r.format(-Math.floor(s / 86400), "day");
    } catch {
        return "";
    }
};

export default function History({ onMuat }) {
    const { t, locale, lang } = useI18n();
    const rh = RH_LABELS[lang] || RH_LABELS.id;
    const [list, setList] = useState(load);
    const [confirmClear, setConfirmClear] = useState(false);

    useEffect(() => {
        const refresh = () => setList(load());
        window.addEventListener("refundaja-riwayat", refresh);
        return () => window.removeEventListener("refundaja-riwayat", refresh);
    }, []);

    const hapus = (id) => {
        save(load().filter((e) => e.id !== id));
        window.dispatchEvent(new Event("refundaja-riwayat"));
    };
    const hapusSemua = () => {
        if (!confirmClear) {
            setConfirmClear(true);
            setTimeout(() => setConfirmClear(false), 2500);
            return;
        }
        save([]);
        window.dispatchEvent(new Event("refundaja-riwayat"));
        setConfirmClear(false);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.7 }}
            className="mt-6 rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl sm:p-8"
            data-testid="riwayat"
        >
            <div className="mb-5 flex items-center justify-between">
                <h3 className="flex items-center gap-2.5 font-display text-xl font-bold">
                    <HistoryIcon size={18} className="text-brand-violet" /> {rh.rh1}
                    {list.length > 0 && (
                        <span className="rounded-full bg-white/10 px-2.5 py-0.5 font-mono text-xs">{list.length}</span>
                    )}
                </h3>
                {list.length > 0 && (
                    <button
                        onClick={hapusSemua}
                        data-testid="riwayat-hapus-semua"
                        className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
                            confirmClear
                                ? "border-red-400/50 text-red-300"
                                : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
                        }`}
                    >
                        <Trash2 size={13} /> {confirmClear ? rh.rh5 : rh.rh2}
                    </button>
                )}
            </div>

            {list.length === 0 ? (
                <p className="text-sm text-zinc-600" data-testid="riwayat-kosong">
                    {rh.rh4}
                </p>
            ) : (
                <ul className="space-y-2.5">
                    <AnimatePresence initial={false}>
                        {list.map((e) => (
                            <motion.li
                                key={e.id}
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.25 }}
                                className="flex items-center gap-4 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-3.5"
                                data-testid={`riwayat-item-${e.id}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-bold text-white">
                                        {e.nama || t.f1} · <span className="text-gradient">Rp {fmt(e.hasil)}</span>
                                    </p>
                                    <p className="mt-0.5 truncate font-mono text-[11px] text-zinc-500">
                                        Rp {fmt(e.harga)} · {e.total} hari · sisa {e.sisa} · {Math.round(e.rate * 100)}%
                                    </p>
                                </div>
                                <span className="hidden shrink-0 font-mono text-[10px] text-zinc-600 sm:block">
                                    {ago(locale, e.at)}
                                </span>
                                <button
                                    onClick={() => onMuat(e)}
                                    data-testid={`riwayat-muat-${e.id}`}
                                    className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-zinc-300 transition-colors duration-200 hover:border-white/40 hover:text-white"
                                >
                                    <RotateCcw size={12} /> {rh.rh3}
                                </button>
                                <button
                                    onClick={() => hapus(e.id)}
                                    data-testid={`riwayat-hapus-${e.id}`}
                                    aria-label="Hapus"
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-zinc-500 transition-colors duration-200 hover:bg-white/10 hover:text-red-300"
                                >
                                    <X size={14} />
                                </button>
                            </motion.li>
                        ))}
                    </AnimatePresence>
                </ul>
            )}
        </motion.div>
    );
}
